'use client';

import { useEffect, useState } from 'react';
import { BlockRoot, PortalLink, useLocale } from '@akademi/site-builder-runtime';
import { WorkspacePageStyles } from '@solar/workspace-ui/src/workspace/WorkspacePageStyles';

/**
 * `/projects` — retired on 7 October 2026, and now a forwarding address.
 *
 * The shop and the planner both grew a home for a project, so a shopper who made
 * one in the planner had to leave it to manage one, and the two pages drifted into
 * two readings of the same data. The owner settled it: the shop owns saved lists
 * and the cart, the planner owns projects. Everything this page did — designs,
 * equipment, quotes, files, activity, new sites — is on the planner's own project
 * page, built from the same components this page used.
 *
 * The page itself stays published rather than being deleted, because its address
 * is in sent quotes, saved bookmarks and the shop's own links. It maps each of
 * those onto the planner's equivalent instead of answering 404.
 */
const TABS: Record<string, string> = { overview: 'designs', designs: 'designs', equipment: 'equipment', quotes: 'quotes', files: 'files', activity: 'activity' };

/** The planner address that answers an old `/projects` query string. */
export function plannerDestination(search: string): string {
  const params = new URLSearchParams(search);
  const project = params.get('project');
  if (project) {
    const carried = new URLSearchParams();
    const tab = TABS[params.get('tab') ?? 'overview'];
    if (tab && tab !== 'designs') carried.set('tab', tab);
    const requestsPage = Number(params.get('requestsPage') ?? 0);
    if (Number.isSafeInteger(requestsPage) && requestsPage > 0) carried.set('requestsPage', String(requestsPage));
    return `/solar/planner/p/${encodeURIComponent(project)}${carried.size ? `?${carried}` : ''}`;
  }
  // No project named: the list, with whatever the shopper was searching for. `new=1`
  // needs nothing carried — the planner's create form is always on its landing.
  const carried = new URLSearchParams();
  const q = params.get('q'); if (q?.trim()) carried.set('q', q.trim());
  const page = Number(params.get('page') ?? 0);
  if (Number.isSafeInteger(page) && page > 0) carried.set('page', String(page));
  return `/solar/planner${carried.size ? `?${carried}` : ''}`;
}

export interface SolarProjectsProps { apiRoot?: string; language?: 'en' | 'tr'; initialProjectId?: string; initialTab?: string }

export function SolarProjects({ language }: SolarProjectsProps) {
  const hostLocale = useLocale().locale;
  const locale = language ?? (hostLocale.toLowerCase().startsWith('tr') ? 'tr' : 'en');
  const [destination, setDestination] = useState('/solar/planner');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const target = plannerDestination(window.location.search);
    setDestination(target);
    // Replace, so Back from the planner leaves for wherever the shopper came from
    // rather than returning here and forwarding them again.
    window.location.replace(target);
  }, []);

  return <BlockRoot as="section">
    <WorkspacePageStyles />
    <section className="solar-projects" aria-labelledby="projects-heading">
      <h1 id="projects-heading">{locale === 'tr' ? 'Projeleriniz' : 'Your projects'}</h1>
      <p role="status">{locale === 'tr'
        ? 'Projeleriniz artık güneş planlayıcısında. Yönlendiriliyorsunuz…'
        : 'Your projects now live in the solar planner. Taking you there…'}</p>
      <div className="workspace-page-actions">
        <a className="workspace-page-action workspace-page-primary" href={destination}>
          {locale === 'tr' ? 'Planlayıcıyı aç' : 'Open the planner'}
        </a>
        <PortalLink className="workspace-page-action" href="/account/lists">
          {locale === 'tr' ? 'Kayıtlı listeler' : 'Saved lists'}
        </PortalLink>
      </div>
    </section>
  </BlockRoot>;
}
